import type { WorkflowSchedule } from '../../routines.types';
import type { ScheduleIntl } from './schedule-intl';
import { formatWeekdayLabel, parseWeekdayCron } from './weekday-cron';

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** Local time-of-day, e.g. "8:00 AM". Empty on parse failure. */
function formatTimeOfDay(
  intl: ScheduleIntl,
  isoString: string,
  timeZone: string
): string {
  try {
    return intl.formatTime(new Date(isoString), {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    });
  } catch {
    return '';
  }
}

/** Pluralised weekday name, e.g. "Mondays". Falls back to "Weekly". */
function formatWeekdayName(
  intl: ScheduleIntl,
  isoString: string,
  timeZone: string
): string {
  try {
    const day = dayOfWeekInTz(new Date(isoString), timeZone);
    return formatWeekdayLabel(intl, [day]);
  } catch {
    return intl.formatMessage({
      defaultMessage: 'Weekly',
      id: 'nfvTy0RZMv',
      description: 'Fallback cadence for a weekly routine schedule',
    });
  }
}

/**
 * Compact cadence summary for a card chip, e.g. "Daily · 8:00 AM",
 * "Mondays · 9:30 AM", "Hourly". (Distinct from {@link formatNextRun}, which
 * describes the *next* occurrence relative to now.)
 */
export function formatScheduleSummary(
  intl: ScheduleIntl,
  schedule: WorkflowSchedule
): string {
  const { frequency, start_at, timezone } = schedule.spec;
  const timeZone =
    timezone || intl.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const time = formatTimeOfDay(intl, start_at, timeZone);

  switch (frequency.kind) {
    case 'hourly':
      return intl.formatMessage({
        defaultMessage: 'Hourly',
        id: 'AWpZv89lv2',
        description: 'Cadence label for a routine that runs every hour',
      });
    case 'daily':
      return time
        ? intl.formatMessage(
            {
              defaultMessage: 'Daily · {time}',
              id: '7awo/sYOF6',
              description: 'Cadence label for a daily routine with its run time',
            },
            { time }
          )
        : intl.formatMessage({
            defaultMessage: 'Daily',
            id: 'C1imfP3/Gj',
            description: 'Cadence label for a routine that runs daily',
          });
    case 'weekly': {
      const day = formatWeekdayName(intl, start_at, timeZone);
      return time
        ? intl.formatMessage(
            {
              defaultMessage: '{day} · {time}',
              id: 'S8NQarS2LO',
              description: 'Cadence label with recurring weekdays and run time',
            },
            { day, time }
          )
        : day;
    }
    case 'cron': {
      const parsed = parseWeekdayCron(frequency.cron_expression);
      if (parsed) {
        const dayLabel = formatWeekdayLabel(intl, parsed.days);
        return time
          ? intl.formatMessage(
              {
                defaultMessage: '{days} · {time}',
                id: 'vlXtPBvYPs',
                description: 'Cadence label with scheduled weekdays and run time',
              },
              { days: dayLabel, time }
            )
          : dayLabel;
      }
      return scheduledLabel(intl);
    }
    default:
      return scheduledLabel(intl);
  }
}

/** Extract hour and minute for a Date in the given IANA timezone. */
function timePartsInTz(date: Date, tz: string): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24;
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return { hour, minute };
}

/** Get day-of-week (0=Sun..6=Sat) for a Date in the given IANA timezone. */
function dayOfWeekInTz(date: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
  }).formatToParts(date);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return map[weekday] ?? date.getDay();
}

/** First hourly fire strictly after `now`, or the start itself if it is still ahead. */
function nextHourlyFire(start: Date, now: Date): Date {
  if (start.getTime() > now.getTime()) return start;
  const periods = Math.floor((now.getTime() - start.getTime()) / HOUR_MS) + 1;
  return new Date(start.getTime() + periods * HOUR_MS);
}

function formatClockTime(intl: ScheduleIntl, date: Date, tz: string): string {
  return intl.formatTime(date, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

function scheduledLabel(intl: ScheduleIntl): string {
  return intl.formatMessage({
    defaultMessage: 'Scheduled',
    id: 'YtTcweqmer',
    description: 'Fallback label when the next routine run cannot be determined',
  });
}

function tomorrowAt(intl: ScheduleIntl, time: string): string {
  return intl.formatMessage(
    {
      defaultMessage: 'Tomorrow {time}',
      id: 'gRC5sJn5w2',
      description: 'Next routine run occurs tomorrow at the given time',
    },
    { time }
  );
}

function dayAt(intl: ScheduleIntl, day: string, time: string): string {
  return intl.formatMessage(
    {
      defaultMessage: '{day} {time}',
      id: 'nN++7z44Qe',
      description: 'Next routine run occurs on the given weekday and time',
    },
    { day, time }
  );
}

export function formatNextRun(intl: ScheduleIntl, schedule: WorkflowSchedule): string {
  try {
    const { frequency, start_at, timezone } = schedule.spec;
    const tz =
      timezone || intl.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const start = new Date(start_at);

    const time = formatClockTime(intl, start, tz);

    switch (frequency.kind) {
      case 'hourly': {
        const next = nextHourlyFire(start, now);
        const clock = formatClockTime(intl, next, tz);
        // An hourly schedule that has not started yet can be days out, so
        // "tomorrow" is only safe once the fire is inside 24 hours.
        if (next.getTime() - now.getTime() > DAY_MS) {
          const dayLabel = intl.formatDate(next, {
            weekday: 'long',
            timeZone: tz,
          });
          return dayAt(intl, dayLabel, clock);
        }
        const crossesMidnight = dayOfWeekInTz(next, tz) !== dayOfWeekInTz(now, tz);
        return crossesMidnight ? tomorrowAt(intl, clock) : clock;
      }
      case 'daily': {
        const startParts = timePartsInTz(start, tz);
        const nowParts = timePartsInTz(now, tz);
        const todayFired =
          startParts.hour * 60 + startParts.minute <=
          nowParts.hour * 60 + nowParts.minute;
        return todayFired
          ? tomorrowAt(intl, time)
          : intl.formatMessage(
              {
                defaultMessage: 'Today {time}',
                id: '2F7rQIx6vc',
                description: 'Next routine run occurs today at the given time',
              },
              { time }
            );
      }
      case 'weekly': {
        const targetDay = dayOfWeekInTz(start, tz);
        const currentDay = dayOfWeekInTz(now, tz);
        const daysUntil = (targetDay - currentDay + 7) % 7 || 7;
        if (daysUntil === 1) return tomorrowAt(intl, time);
        const dayLabel = intl.formatDate(start, {
          weekday: 'long',
          timeZone: tz,
        });
        return dayAt(intl, dayLabel, time);
      }
      case 'cron': {
        const parsed = parseWeekdayCron(frequency.cron_expression);
        if (parsed) {
          const currentDay = dayOfWeekInTz(now, tz);
          const sortedDays = [...parsed.days].sort((a, b) => a - b);
          const nextDay = sortedDays.find((d) => d > currentDay) ?? sortedDays[0]!;
          const daysUntil = (nextDay - currentDay + 7) % 7 || 7;
          if (daysUntil === 1) return tomorrowAt(intl, time);
          const dayLabel = intl.formatDate(new Date(Date.UTC(2023, 0, 1 + nextDay)), {
            weekday: 'long',
            timeZone: 'UTC',
          });
          return dayAt(intl, dayLabel, time);
        }
        return scheduledLabel(intl);
      }
      default:
        return scheduledLabel(intl);
    }
  } catch {
    return scheduledLabel(intl);
  }
}
